<template>
    <v-container class="pa-0 coach-profile">
        <back-button class="mb-3" />

        <skeleton-loader v-if="loading" type="company-coach-profile" />

        <div v-else-if="coach" class="ficha-grid">

            <!-- LEFT -->
            <div class="left-col">
                <div class="coach-card">
                    <v-img v-if="coachImg" :src="coachImg" aspect-ratio="1" class="coach-photo" />
                    <div v-else class="coach-photo coach-photo--initials" :style="avatarGradient">{{ initials }}</div>
                    <div class="coach-info">
                        <div class="coach-name">{{ coachName }}</div>
                        <div v-if="coach.email" class="coach-sub">{{ coach.email }}</div>
                        <div class="coach-tags">
                            <span class="tag" :class="isActive ? 'tag-active' : 'tag-inactive'">{{ statusLabel }}</span>
                            <span v-if="yearsExperience" class="tag tag-exp">{{ experienceLabel }}</span>
                        </div>
                    </div>
                </div>

                <div class="stats-card">
                    <div v-for="stat in stats" :key="stat.label" class="stat-row">
                        <span class="stat-lbl">{{ stat.label }}</span>
                        <span class="stat-val">{{ stat.value }}</span>
                    </div>
                </div>
            </div>

            <!-- CENTER -->
            <div class="center-col">
                <div class="acc-group">
                    <expanded-sections :sections="sections" :value="openSection" eager>
                        <template #data>
                            <div class="field-list">
                                <div v-for="info in coachDetails" :key="info.label" class="field-row">
                                    <span class="field-lbl">{{ info.label }}</span>
                                    <span class="field-val">{{ info.value }}</span>
                                </div>
                            </div>
                        </template>
                        <template #students>
                            <div v-if="students.length === 0" class="empty-text">{{ $t('common.no_students') }}</div>
                            <div v-else class="student-list">
                                <div v-for="student in students" :key="student.id" class="student-row"
                                    @click="toStudentProfile(student)">
                                    <div v-if="student.avatar" class="student-avatar"
                                        :style="{ backgroundImage: `url('${student.avatar}')` }" />
                                    <div v-else class="student-avatar-initials" :style="student.avatarGradient">
                                        {{ student.initials }}
                                    </div>
                                    <truncated-text class="student-name" :text="student.name" />
                                    <v-icon size="16" color="txtMuted">mdi-chevron-right</v-icon>
                                </div>
                                <div v-if="hasMoreStudents" class="student-more" @click="toAdminStudents">
                                    {{ $t('company.see_all_students', { n: activeStudents }) }}
                                </div>
                            </div>
                        </template>
                    </expanded-sections>
                </div>

                <div class="danger-zone">
                    <v-btn outlined color="error" class="danger-btn text-none" @click="showUnlink">
                        <v-icon size="16" left>mdi-link-variant-off</v-icon>
                        {{ $t('admin.unlink_coach') }}
                    </v-btn>
                </div>
            </div>

        </div>

        <kimsa-confirm ref="unlink" alert-mode show-cancel :title="$t('admin.unlink_coach')"
            :content="$t('admin.unlink_coach_text')" :confirm-label="$t('admin.unlink')" :loading="loadingUnlink"
            :persistent="false" @confirm="onConfirmUnlink" :close-on-confirm="false" />

    </v-container>
</template>

<script>
import { db } from "@/utils/Firebase";
import { UserIndex } from "@/utils/Algolia";
import { getInitials, getAvatarGradient, toDate } from "@/utils/Common";
import { fetchCoachWeeklyRoutines } from "@/utils/Statistics";
import BackButton from "@/components/common/BackButton";
import SkeletonLoader from "@/components/common/SkeletonLoader";
import ExpandedSections from "@/components/common/ExpandedSections";
import TruncatedText from "@/components/common/TruncatedText";
import KimsaConfirm from "@/components/common/KimsaConfirm";

const STUDENTS_PREVIEW = 6
const SECTION_DATA = 0
const SECTION_STUDENTS = 1

let sectionToRestore = null

export default {
    name: "CompanyCoachProfile",
    components: { BackButton, SkeletonLoader, ExpandedSections, TruncatedText, KimsaConfirm },
    data() {
        return {
            loading: false,
            loadingUnlink: false,
            coach: null,
            students: [],
            weeklyRoutines: 0,
            openSection: sectionToRestore !== null ? sectionToRestore : SECTION_DATA,
        }
    },
    computed: {
        sections(vm = this) {
            return [
                { title: vm.$t('common.data'), value: 'data' },
                { title: vm.$t('common.students'), value: 'students' },
            ]
        },
        coachId(vm = this) {
            return vm.$route.params.uid || null
        },
        coachName(vm = this) {
            return vm.coach?.name || vm.coach?.email || vm._noAvailableName
        },
        coachImg(vm = this) {
            return vm.coach?.avatar || null
        },
        initials(vm = this) {
            return getInitials(vm.coachName)
        },
        avatarGradient(vm = this) {
            return getAvatarGradient(vm.coachId || vm.coachName || '')
        },
        isActive(vm = this) {
            return vm.coach?.coachSubscription?.paused !== true
        },
        statusLabel(vm = this) {
            return vm.isActive ? vm.$t('common.active') : vm.$t('common.inactive')
        },
        yearsExperience(vm = this) {
            return Number(vm.coach?.experience || 0)
        },
        experienceLabel(vm = this) {
            return vm.$tc('company.years_experience_count', vm.yearsExperience, { n: vm.yearsExperience })
        },
        activeStudents(vm = this) {
            return Number(vm.coach?.activeStudents || 0)
        },
        hasMoreStudents(vm = this) {
            return vm.activeStudents > vm.students.length
        },
        stats(vm = this) {
            return [
                { label: vm.$t('coach.active_students'), value: vm.activeStudents },
                { label: vm.$t('coach.assigned_routines'), value: vm.weeklyRoutines },
                { label: vm.$t('coach.groups_students'), value: Number(vm.coach?.groupsQuantity || 0) },
                { label: vm.$t('coach.teams_students'), value: Number(vm.coach?.teamsQuantity || 0) },
            ]
        },
        birthDate(vm = this) {
            const date = toDate(vm.coach?.birthDate)
            if (!date) return vm.$t('common.no_set')
            return vm.$moment(date).format('DD-MM-YYYY')
        },
        age(vm = this) {
            const date = toDate(vm.coach?.birthDate)
            if (!date) return vm.$t('common.no_set')
            return vm.$moment().diff(vm.$moment(date), 'years')
        },
        phone(vm = this) {
            if (!vm.coach?.phone) return vm.$t('common.no_set')
            const code = vm.coach?.phoneCode || vm.coach?.country?.phoneCode || ''
            return `${code} ${vm.coach.phone}`.trim()
        },
        coachDetails(vm = this) {
            return [
                { label: vm.$t('common.email'), value: vm.coach?.email || vm.$t('common.no_set') },
                { label: vm.$t('common.phone'), value: vm.phone },
                { label: vm.$t('common.country'), value: vm.countryLabel(vm.coach?.country?.name) || vm.$t('common.no_set') },
                { label: vm.$t('common.age'), value: vm.age },
                { label: vm.$t('common.birth_date'), value: vm.birthDate },
                { label: vm.$t('common.work_in'), value: vm.coach?.coachSubscription?.companyName || '' },
            ]
        },
    },
    mounted() {
        sectionToRestore = null
        this.loadCoach()
    },
    methods: {
        async loadCoach() {
            const vm = this
            if (!vm.coachId) return
            vm.loading = true
            try {
                const coachRef = await db.collection('users').doc(vm.coachId).get()
                if (!coachRef.exists) return
                vm.coach = { id: coachRef.id, ...coachRef.data() }
                await Promise.all([vm.loadStudents(), vm.loadWeeklyRoutines()])
            } catch (error) {
                vm.toast(vm.$t('common.error_generic'), 'error')
            } finally {
                vm.loading = false
            }
        },
        async loadWeeklyRoutines() {
            const vm = this
            try {
                vm.weeklyRoutines = await fetchCoachWeeklyRoutines(vm.coachId)
            } catch (error) {
                console.log('loadWeeklyRoutines error', error)
                vm.weeklyRoutines = 0
            }
        },
        async loadStudents() {
            const vm = this
            try {
                const { hits } = await UserIndex.search('', {
                    filters: `studentSubscription.coachId: ${vm.coachId} AND studentSubscription.paused != 1`,
                    hitsPerPage: STUDENTS_PREVIEW,
                })
                vm.students = hits.map(hit => {
                    const name = hit.name || hit.email || vm._noAvailableName
                    return {
                        id: hit.objectID,
                        name,
                        avatar: hit.avatar || null,
                        initials: getInitials(name),
                        avatarGradient: getAvatarGradient(hit.objectID || name),
                    }
                })
            } catch (error) {
                console.log('loadStudents error', error)
            }
        },
        toStudentProfile(student) {
            sectionToRestore = SECTION_STUDENTS
            this.pushRoute({ name: 'company.students.profile', params: { uid: student.id } })
        },
        toAdminStudents() {
            this.pushRoute({ name: 'company.admin', query: { tab: 'students', coachId: this.coachId } })
        },
        showUnlink() {
            this.$refs.unlink.show()
        },
        async onConfirmUnlink() {
            const vm = this
            vm.loadingUnlink = true
            try {
                if (vm.coach?.coachSubscription?.id) {
                    await db.collection('subscriptions').doc(vm.coach.coachSubscription.id).delete()
                }
                await db.collection('users').doc(vm.coachId).update({
                    accountType: 'coach',
                    coachSubscription: null,
                })
                vm.$refs.unlink?.hide()
                vm.pushRoute({ name: 'company.admin' })
            } catch (error) {
                vm.toast(vm.$t('common.error_generic'), 'error')
            } finally {
                vm.loadingUnlink = false
            }
        },
    },
}
</script>

<style scoped>
.ficha-grid {
    display: grid;
    grid-template-columns: 260px 1fr;
    gap: 20px;
    align-items: start;
}

.left-col,
.center-col {
    display: flex;
    flex-direction: column;
    gap: 12px;
    min-width: 0;
}

.coach-card {
    background: var(--v-surface-base);
    border: 1px solid var(--v-outline-base);
    border-radius: 14px;
    overflow: hidden;
}

.coach-photo {
    width: 100%;
    display: block;
}

.coach-photo--initials {
    aspect-ratio: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 48px;
    font-weight: 800;
    letter-spacing: 0.02em;
}

.coach-info {
    padding: 14px 16px 16px;
}

.coach-name {
    font-size: 18px;
    font-weight: 700;
    color: var(--v-txtPrimary-base);
    margin-bottom: 4px;
}

.coach-sub {
    font-size: 12px;
    color: var(--v-txtMuted-base);
    margin-bottom: 10px;
    word-break: break-all;
}

.coach-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
}

.tag {
    font-size: 10px;
    font-weight: 600;
    padding: 3px 8px;
    border-radius: 20px;
    letter-spacing: 0.04em;
}

.tag-active {
    background: rgba(51, 153, 153, 0.15);
    color: var(--v-accent-base);
}

.tag-inactive {
    background: rgba(232, 92, 92, 0.15);
    color: var(--v-error-base);
}

.tag-exp {
    background: rgba(0, 153, 102, 0.15);
    color: #009966;
}

.stats-card {
    background: var(--v-surface-base);
    border: 1px solid var(--v-outline-base);
    border-radius: 14px;
    padding: 6px 16px;
}

.stat-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 9px 0;
    border-bottom: 1px solid var(--v-outline-base);
}

.stat-row:last-child {
    border-bottom: none;
}

.stat-lbl {
    font-size: 12px;
    color: var(--v-txtSecondary-base);
}

.stat-val {
    font-size: 15px;
    font-weight: 700;
    color: var(--v-txtPrimary-base);
}

.acc-group {
    background: var(--v-surface-base);
    border: 1px solid var(--v-outline-base);
    border-radius: 14px;
    overflow: hidden;
}

.field-list {
    display: flex;
    flex-direction: column;
}

.field-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 8px 0;
}

.field-lbl {
    font-size: 12px;
    color: var(--v-txtSecondary-base);
}

.field-val {
    font-size: 13px;
    color: var(--v-txtPrimary-base);
    text-align: right;
    word-break: break-word;
}

.student-list {
    display: flex;
    flex-direction: column;
}

.student-row {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 0;
    cursor: pointer;
}

.student-row:hover .student-name {
    color: var(--v-accent-base);
}

.student-avatar,
.student-avatar-initials {
    width: 32px;
    height: 32px;
    border-radius: 8px;
    flex-shrink: 0;
}

.student-avatar {
    background-size: cover;
    background-position: center;
    background-color: var(--v-surface2-base);
}

.student-avatar-initials {
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 11px;
    font-weight: 700;
}

.student-name {
    flex: 1;
    min-width: 0;
    font-size: 13px;
    font-weight: 500;
    color: var(--v-txtPrimary-base);
}

.student-more {
    font-size: 12px;
    font-weight: 600;
    color: var(--v-accent-base);
    cursor: pointer;
    padding: 10px 0 4px;
}

.empty-text {
    font-size: 12px;
    color: var(--v-txtMuted-base);
    font-style: italic;
    padding: 4px 0;
}

.danger-zone {
    display: flex;
    justify-content: flex-end;
}

.danger-btn {
    border-color: rgba(232, 92, 92, 0.4) !important;
}

@media (max-width: 960px) {
    .ficha-grid {
        grid-template-columns: 1fr;
    }
}
</style>
